import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  CameraAlt,
  PhotoCamera,
  Cameraswitch,
  Close,
} from "@mui/icons-material";

const VideoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: "#000",
  position: "relative",
  minHeight: "400px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const Video = styled("video")({
  width: "100%",
  height: "auto",
  maxHeight: "70vh",
  display: "block",
});

const HiddenCanvas = styled("canvas")({
  display: "none",
});

const ButtonRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  justifyContent: "center",
  flexWrap: "wrap",
}));

/**
 * CameraCapture Component
 *
 * A responsive modal that opens the device camera and lets the user
 * capture a photo. Supports front/back camera toggle on mobile devices.
 *
 * Props:
 *  - open      : boolean  - controls modal visibility
 *  - onClose   : function - called when the modal should close
 *  - onCapture : function - called with the captured File object
 */
const CameraCapture = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [isReady, setIsReady] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsReady(false);
  }, []);

  const startStream = useCallback(async (mode) => {
    setLoading(true);
    setErrorMessage("");
    setIsReady(false);

    // Guard: check API availability
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMessage(
        "Camera API not supported by this browser. Please use a modern browser over HTTPS.",
      );
      setLoading(false);
      return;
    }

    // Try preferred facingMode first, then fallback to any camera
    const attempts = [
      { video: { facingMode: { ideal: mode } }, audio: false },
      { video: { facingMode: mode }, audio: false },
      { video: true, audio: false },
    ];

    let stream = null;
    let lastError = null;

    for (const constraints of attempts) {
      try {
        // eslint-disable-next-line no-await-in-loop
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (err) {
        lastError = err;
      }
    }

    if (!stream) {
      const message =
        lastError?.name === "NotAllowedError"
          ? "Camera permission denied. Please allow camera access in your browser settings."
          : lastError?.name === "NotFoundError"
            ? "No camera device found on this device."
            : `Unable to access camera: ${lastError?.message || "Unknown error"}`;
      setErrorMessage(message);
      setLoading(false);
      return;
    }

    streamRef.current = stream;

    if (videoRef.current) {
      videoRef.current.srcObject = stream;

      // Wait for metadata and start playback
      const onLoaded = async () => {
        try {
          await videoRef.current.play();
          setIsReady(true);
          setLoading(false);
        } catch (playError) {
          console.error("Video play error:", playError);
          setErrorMessage("Unable to start video preview.");
          setLoading(false);
        }
      };

      videoRef.current.onloadedmetadata = onLoaded;
    }
  }, []);

  // Start/stop camera when modal opens/closes or facingMode changes
  useEffect(() => {
    if (open) {
      startStream(facingMode);
    } else {
      stopStream();
    }

    return () => {
      stopStream();
    };
  }, [open, facingMode, startStream, stopStream]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current || !isReady) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth;
    const height = video.videoHeight;

    if (!width || !height) {
      setErrorMessage("Video not ready yet. Please wait a moment.");
      return;
    }

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setErrorMessage("Failed to capture image.");
          return;
        }
        const fileName = `camera-${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: "image/jpeg" });
        onCapture(file);
        handleClose();
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleToggleCamera = () => {
    setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
  };

  const handleClose = () => {
    stopStream();
    setErrorMessage("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { m: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CameraAlt />
          <Typography variant="h6">Capture Photo</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <VideoContainer>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "#fff",
                zIndex: 2,
              }}
            >
              <CircularProgress color="inherit" />
              <Typography variant="body2">Starting camera...</Typography>
            </Box>
          )}

          {errorMessage && !loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "#fff",
                textAlign: "center",
                p: 3,
                zIndex: 2,
              }}
            >
              <Typography variant="h3">📷</Typography>
              <Typography variant="body2">{errorMessage}</Typography>
            </Box>
          )}

          <Video ref={videoRef} autoPlay playsInline muted />
          <HiddenCanvas ref={canvasRef} />
        </VideoContainer>

        <ButtonRow>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PhotoCamera />}
            onClick={handleCapture}
            disabled={!isReady || loading}
          >
            Capture
          </Button>
          <Button
            variant="outlined"
            startIcon={<Cameraswitch />}
            onClick={handleToggleCamera}
            disabled={loading}
          >
            {facingMode === "environment" ? "Front" : "Back"} Camera
          </Button>
          <Button variant="outlined" color="error" onClick={handleClose}>
            Cancel
          </Button>
        </ButtonRow>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCapture;
